import React, { ReactNode, useState } from 'react';
import { SubscriberTask as SubscriberTaskType, SubscriberTaskStatus } from 'types/electionService';
import { isActiveSubscriberTask } from 'global/services-helpers/electionService';
import { ChevronDown, ChevronUp, IconCheckmark, InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Collapse, colors } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { ChecklistOptionPresenter } from 'store/modules/subscriber/subscriber-tasks/subscriberEntryNavigationModule';
import { ChecklistButtonStateText } from 'types/enrollmentService';
import { SUBSCRIBER_TASK_TEST_IDS } from 'components/subscriber/constants';
import {
  ChecklistStepsStyled,
  TaskHeaderContentStyled,
  TaskHeaderStyled,
  TaskStyled,
  TaskSubtitleStyled,
  TaskTitleBlockStyled,
  TaskTitleStyled,
  StepCardStyled,
} from './styles';

interface Props {
  task: SubscriberTaskType;
  checklistOptions: ChecklistOptionPresenter[];
}

const TASK_STATUS_ICON_COLOR: Record<SubscriberTaskStatus, string> = {
  [SubscriberTaskStatus.READY_TO_BEGIN]: colors.red[600],
  [SubscriberTaskStatus.IN_PROGRESS]: colors.yellow[600],
  [SubscriberTaskStatus.DONE]: colors.green[600],
  [SubscriberTaskStatus.CLOSED]: colors.neutral[600],
};

const SubscriberTaskStatusIcon: React.FC<{ status: SubscriberTaskStatus }> = ({ status }) => {
  const color = TASK_STATUS_ICON_COLOR[status];

  if (status === SubscriberTaskStatus.DONE || status === SubscriberTaskStatus.CLOSED) {
    return (
      <IconCheckmark height="4.2rem" width="4.2rem" color={color} data-test-id={SUBSCRIBER_TASK_TEST_IDS.STATUS_ICON} />
    );
  }

  return (
    <InfoButton
      viewBox="-1 -1 21 19"
      height="4.2rem"
      width="4.2rem"
      color={color}
      data-test-id={SUBSCRIBER_TASK_TEST_IDS.STATUS_ICON}
    />
  );
};

const getChevronIcon = (isExpanded: boolean): ReactNode =>
  isExpanded ? (
    <ChevronUp
      width="3rem"
      height="3rem"
      data-test-id={SUBSCRIBER_TASK_TEST_IDS.CHEVRON_UP}
      color={colors.neutral[700]}
    />
  ) : (
    <ChevronDown
      width="3rem"
      height="3rem"
      data-test-id={SUBSCRIBER_TASK_TEST_IDS.CHEVRON_DOWN}
      color={colors.neutral[700]}
    />
  );

const SubscriberTask: React.FC<Props> = observer(({ task, checklistOptions }) => {
  const { entityId, name, description, status } = task;
  const isLocked = status === SubscriberTaskStatus.CLOSED;
  const hasChecklist = checklistOptions.length > 0;
  const [isExpanded, setIsExpanded] = useState(isActiveSubscriberTask(task));

  const handleToggle = (): void => {
    if (!hasChecklist) {
      return;
    }
    setIsExpanded((prev) => !prev);
  };

  return (
    <TaskStyled $isExpanded={isExpanded} data-test-id={`subscriber-task-${entityId}`}>
      <TaskHeaderStyled
        as={hasChecklist ? 'button' : 'div'}
        type={hasChecklist ? 'button' : undefined}
        onClick={handleToggle}
        $isExpanded={isExpanded && hasChecklist}
        $isCollapsible={hasChecklist}
        data-test-id={SUBSCRIBER_TASK_TEST_IDS.TOGGLE}
      >
        <TaskHeaderContentStyled>
          <SubscriberTaskStatusIcon status={status} />
          <TaskTitleBlockStyled>
            <TaskTitleStyled data-test-id={SUBSCRIBER_TASK_TEST_IDS.TITLE}>{name}</TaskTitleStyled>
            <TaskSubtitleStyled data-test-id={SUBSCRIBER_TASK_TEST_IDS.DESCRIPTION_VALUE}>
              {description}
            </TaskSubtitleStyled>
          </TaskTitleBlockStyled>
        </TaskHeaderContentStyled>
        {hasChecklist ? getChevronIcon(isExpanded) : null}
      </TaskHeaderStyled>
      {hasChecklist && (
        <Collapse show={isExpanded} data-test-id={SUBSCRIBER_TASK_TEST_IDS.COLLAPSE}>
          <ChecklistStepsStyled>
            {checklistOptions.map((option) => (
              <StepCardStyled
                key={`${option.card}`}
                data-test-id={`step-card-${option.card}`}
                title={option.title}
                text={option.text}
                buttonState={isLocked ? ChecklistButtonStateText.UNAVAILABLE : option.buttonState}
                completionStatus={option.completionStatus}
                onActionClick={isLocked ? (): void => undefined : option.onActionClick}
              />
            ))}
          </ChecklistStepsStyled>
        </Collapse>
      )}
    </TaskStyled>
  );
});

export default SubscriberTask;
